import React from "react";

import { FormikProps, getIn, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import Button from "components/Button";
import { Label } from "components/Form/FormikControls/Label";
import { withNamespace } from "components/Form/FormikControls/lib";

import { AlternativeType } from "constants/businesses";
import { colors } from "constants/theme";

import { getDeployments, getSaleyards } from "selectors";

export interface Alternative {
  source: {
    type: string;
    id: string;
  };
  [key: string]: unknown;
}

export const getAlternateColor = <TValues extends object>(
  alternatives: Alternative[],
  values: TValues,
  altField: string,
  subField: string | null | undefined,
  overrideFieldValue: unknown,
): string => {
  const alternativeValues = alternatives
    .map(alt => (subField ? alt[altField]?.[subField] : alt[altField])) // We want to allow false, but filter out other falsey values
    .filter(alt => alt !== null && alt !== undefined && alt !== "");
  const fieldValue =
    overrideFieldValue ||
    getIn(values, subField ? withNamespace(altField, subField) : altField);
  if (alternativeValues.every(alt => alt === fieldValue)) {
    return colors.primary;
  }

  return colors.warningOrange;
};

const ValueCell = styled.td`
  padding-left: 10px;
`;

const ButtonCell = styled.td`
  padding-left: 10px;
  padding-top: 2px;
`;

const LargeTextTable = styled.table`
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

function defaultOnClickApply(
  formikProps: FormikProps<unknown>,
  overrideFormikField: string | undefined,
  formikField: string,
  value: unknown,
  _ignoredAlt: unknown,
): void {
  // avoid duplicate validation trigger, skip on setFieldValue, run on setFieldTouched
  formikProps.setFieldValue(overrideFormikField || formikField, value, false);
  formikProps.setFieldTouched(overrideFormikField || formikField, true, true);
}

export const AlternateFieldTextComponent = (
  alternatives: Alternative[],
  altFieldName: string,
  subField: string | null = null,
  overrideFormikField?: string,
  disabledButton?: boolean,
  onClickApply: (
    formikProps: FormikProps<unknown>,
    overrideFormikField: string | undefined,
    formikField: string,
    value: unknown,
    alt: unknown,
  ) => void = defaultOnClickApply,
): React.JSX.Element => {
  const formikProps = useFormikContext<unknown>();
  const deployments = useSelector(getDeployments);
  const saleyards = useSelector(getSaleyards);

  return (
    <LargeTextTable>
      <thead>
        <tr>
          <td>Source</td>
          <ValueCell>Value</ValueCell>
          <ButtonCell> </ButtonCell>
        </tr>
      </thead>
      <tbody>
        {alternatives.map(alt => {
          const value = subField
            ? (alt[altFieldName] as Record<string, unknown>)?.[subField]
            : alt[altFieldName];
          const source =
            alt.source.type === AlternativeType.Deployment
              ? deployments[alt.source.id]?.name
              : saleyards[alt.source.id]?.name;
          const formikField = subField
            ? withNamespace(altFieldName, subField)
            : altFieldName;
          function onClick(): void {
            if (typeof onClickApply === "function") {
              onClickApply(
                formikProps,
                overrideFormikField,
                formikField,
                value,
                alt,
              );
            }
          }

          return (
            <tr key={`${alt.source.id}-${alt.source.type}`}>
              <td>{source}</td>
              <ValueCell>
                {value !== null && value !== undefined
                  ? String(value)
                  : "Not Set"}
              </ValueCell>
              <ButtonCell>
                {onClickApply && (
                  <Button onClick={onClick} disabled={disabledButton}>
                    Apply
                  </Button>
                )}
              </ButtonCell>
            </tr>
          );
        })}
      </tbody>
    </LargeTextTable>
  );
};

export interface AlternativeProps {
  original: {
    alternatives?: Alternative[];
  };
  value: unknown;
}
interface AlternateLabelProps {
  props: AlternativeProps;
  altFieldName: string;
  subField?: string | null;
  overrideFormikField?: string;
  disabledButton?: boolean;
  values: Record<string, unknown>;
}

export const AlternateLabel = (
  props: AlternateLabelProps,
): React.JSX.Element => {
  const {
    props: subProps,
    altFieldName,
    subField = null,
    overrideFormikField,
    disabledButton,
    values,
  } = props;

  const { original, value } = subProps;

  return (
    <Label
      altTooltip={AlternateFieldTextComponent(
        original.alternatives || [],
        altFieldName,
        subField,
        overrideFormikField,
        disabledButton,
      )}
      altColor={getAlternateColor(
        original.alternatives || [],
        values,
        altFieldName,
        subField,
        value,
      )}
    >
      {String(value)}
    </Label>
  );
};
